import type { TicketChannelCondition as TicketChannelConditionType } from "@redotech/redo-flows-common/rules/schemas";
import { useTranslation } from "@redotech/redo-i18n";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { getRuleChannelLabels } from "../../rule-channel-labels";

const channels = Object.values(ConversationPlatform);

const channelItems = (): RedoListItem<ConversationPlatform>[] =>
  channels.map((channel) => ({
    id: channel,
    type: "text" as const,
    text: getRuleChannelLabels()[channel],
    value: channel,
  }));

export interface TicketChannelConditionProps {
  condition: TicketChannelConditionType;
  onChange: (updated: TicketChannelConditionType) => void;
}

export const TicketChannelCondition = memo(function TicketChannelCondition({
  condition,
  onChange,
}: {
  condition: TicketChannelConditionType;
  onChange: (updated: TicketChannelConditionType) => void;
}) {
  const { t } = useTranslation();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<ConversationPlatform>[] = useMemo(() => {
    return (condition.channels || []).map((channel) => ({
      id: channel,
      type: "text" as const,
      text: getRuleChannelLabels()[channel],
      value: channel,
    }));
  }, [condition.channels]);

  const displayItem: RedoListItem<ConversationPlatform> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: t("{{count}} channels selected", { count: selectedItems.length }),
        value: ConversationPlatform.FACEBOOK,
      };
    }, [selectedItems, t]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="secondary">
        {t("Channel", { context: "support conversation channel" })}
      </Text>
      <RedoSingleSelectDropdownInputButton
        isDropdownOpen={dropdownOpen}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        placeholder={t("Select channels")}
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems()}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, channels: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
